import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tag, Loader2, CheckCircle2, Megaphone, AlertCircle, Store } from 'lucide-react';
import { TagMultiInput } from '@/components/ui/TagMultiInput';
import { unwrapApiData } from '@/lib/store-formation';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { isLyrraOne } from '@/lib/platform';
import { fetchLoSellerBilling } from '@/lib/lyrraone-hub';
import { lyrraOneExtraSettingsTabPath } from '@/lib/lyrraone-settings-tabs';
import { TrainerRightPanelHeader } from '@/components/dashboard/TrainerRightPanelHeader';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';

const panelShellWideClass =
  'fixed end-0 top-0 z-50 flex h-[100dvh] w-full max-w-xl flex-col border-s border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] shadow-2xl dark:border-gray-800 dark:bg-gray-900';
const panelBackdropClass = 'fixed inset-0 z-40 bg-black/30 backdrop-blur-sm';
const panelInputClass =
  'w-full rounded-xl border border-[color:var(--inst-player-border,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] px-3 py-2 text-sm text-[color:var(--inst-player-text,#111827)] placeholder:text-[color:var(--inst-player-text-muted,#6b7280)] focus:border-[color:var(--inst-player-primary,#0C1C38)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,transparent)] dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';
const panelLabelClass =
  'mb-1.5 block text-sm font-medium text-[color:var(--inst-player-text,#111827)] dark:text-gray-200';
const btnPrimaryInst = cn(
  '!bg-[color:var(--inst-player-primary,#0C1C38)] hover:!bg-[color:var(--inst-player-primary-hover,#19335E)] !text-white',
  'focus-visible:!ring-[color:var(--inst-player-primary,#0C1C38)]',
);

export interface StoreFormationListing {
  id: string;
  flowId: string;
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  category?: string | null;
  tags?: string[];
  price: number;
  status: 'pending' | 'approved' | 'rejected';
  isPublished: boolean;
  listedOnMarketplace?: boolean;
}

export interface PublishToStorePanelProps {
  flowId: string;
  flowTitle: string;
  flowDescription?: string | null;
  flowCoverImageUrl?: string | null;
  flowStatus: string;
  onClose: () => void;
}

export function PublishToStorePanel({
  flowId,
  flowTitle,
  flowDescription,
  flowCoverImageUrl,
  flowStatus,
  onClose,
}: PublishToStorePanelProps) {
  const { t } = useTranslation();
  const lyrraOne = isLyrraOne();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [listing, setListing] = useState<StoreFormationListing | null>(null);
  const [title, setTitle] = useState(flowTitle);
  const [description, setDescription] = useState(flowDescription ?? '');
  const [tags, setTags] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [price, setPrice] = useState('0');
  const [listedOnMarketplace, setListedOnMarketplace] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [billingComplete, setBillingComplete] = useState<boolean | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp: unknown = await api.get('/store/formations/tags');
        const data = unwrapApiData<string[]>(resp);
        if (!cancelled && Array.isArray(data)) setTagSuggestions(data);
      } catch {
        if (!cancelled) setTagSuggestions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const resp: unknown = await api.get(`/store/formations/by-flow/${flowId}`);
        const wrap = resp as { data?: StoreFormationListing | null };
        const data = wrap?.data ?? (resp as StoreFormationListing | null);
        if (!cancelled && data && typeof data === 'object' && 'id' in data) {
          setListing(data);
          setTitle(data.title);
          setDescription(data.description ?? '');
          setTags(
            data.tags?.length
              ? data.tags
              : data.category?.trim()
                ? [data.category.trim()]
                : [],
          );
          setPrice(String((data.price / 100).toFixed(2)));
          setListedOnMarketplace(Boolean(data.listedOnMarketplace));
        }
      } catch {
        if (!cancelled) setListing(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flowId]);

  useEffect(() => {
    if (!lyrraOne) return;
    void fetchLoSellerBilling()
      .then((d) => setBillingComplete(d.complete))
      .catch(() => setBillingComplete(null));
  }, [lyrraOne]);

  const handlePublish = async () => {
    if (flowStatus !== 'published') {
      setMessage({
        type: 'err',
        text: t('lyrraone.store.mustPublishFirst', "Publiez d'abord la formation avant de configurer la vente."),
      });
      return;
    }
    if (lyrraOne && billingComplete === false) {
      setMessage({
        type: 'err',
        text: t(
          'lyrraone.store.billingRequired',
          'Renseignez vos informations de société avant de mettre en vente.',
        ),
      });
      return;
    }
    const priceCents = Math.round(parseFloat(price.replace(',', '.')) * 100);
    if (!Number.isFinite(priceCents) || priceCents < 0) {
      setMessage({ type: 'err', text: t('lyrraone.store.invalidPrice', 'Prix invalide.') });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const resp: unknown = await api.post(`/store/formations/by-flow/${flowId}/publish`, {
        title: title.trim() || flowTitle,
        description: description.trim() || undefined,
        coverImageUrl: flowCoverImageUrl ?? undefined,
        tags,
        price: priceCents,
        ...(lyrraOne ? { listedOnMarketplace } : {}),
      });
      const wrap = resp as { data?: StoreFormationListing };
      const data = wrap?.data ?? (resp as StoreFormationListing);
      setListing(data);
      setListedOnMarketplace(Boolean(data.listedOnMarketplace));
      setMessage({
        type: 'ok',
        text: lyrraOne
          ? data.listedOnMarketplace
            ? t(
                'lyrraone.store.offerSavedWithMarketplace',
                'Paramètres enregistrés. Votre formation est visible sur votre site, vos funnels et le marketplace LyrraOne.',
              )
            : t(
                'lyrraone.store.offerSaved',
                'Paramètres enregistrés. La formation est visible sur votre site, vos funnels et vos automatisations.',
              )
          : t(
              'lyrraone.store.publishSuccess',
              'Formation soumise au Store LyrraOne. Un administrateur validera votre annonce.',
            ),
      });
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
      const code = apiErr.response?.data?.error?.code;
      const msg = apiErr.response?.data?.error?.message
        ?? (err instanceof Error ? err.message : t('lyrraone.store.publishError', 'Erreur lors de la publication.'));
      if (code === 'SELLER_BILLING_INCOMPLETE') {
        setBillingComplete(false);
      }
      setMessage({ type: 'err', text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (typeof document === 'undefined') return null;

  const panelTitle = lyrraOne
    ? t('lyrraone.store.offerTitle', 'Paramètres de vente')
    : t('lyrraone.store.title', 'Publier sur le Store LyrraOne');

  return createPortal(
    <>
      <div className={panelBackdropClass} onClick={() => !submitting && onClose()} aria-hidden />
      <div className={panelShellWideClass} role="dialog" aria-modal="true" aria-labelledby="publish-store-panel-title">
        <TrainerRightPanelHeader
          icon={Tag}
          title={panelTitle}
          titleId="publish-store-panel-title"
          description={flowTitle}
          onClose={() => !submitting && onClose()}
          closeDisabled={submitting}
          closeAriaLabel={t('common.close', 'Fermer')}
          supportFabPanelMaxWidth="xl"
        />

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
            <p className="text-sm text-[color:var(--inst-player-text-muted,#6b7280)] dark:text-gray-400">
              {lyrraOne
                ? t(
                    'lyrraone.store.offerSubtitle',
                    'Définissez le prix et la présentation de votre formation. Elle apparaît sur votre site web, vos funnels, e-mails et SMS. Vous pouvez aussi la proposer sur le marketplace public LyrraOne.',
                  )
                : t(
                    'lyrraone.store.subtitle',
                    "Mettez votre formation en vente sur le catalogue public LyrraOne. Les apprenants pourront l'acheter directement.",
                  )}
            </p>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-[color:var(--inst-player-primary,#0C1C38)]" />
              </div>
            ) : (
              <>
                {lyrraOne && flowStatus === 'published' && billingComplete === true && (
                  <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    {t('lyrraone.store.visibleOnSite', 'Formation publiée — visible sur votre site et vos canaux.')}
                  </div>
                )}

                {flowStatus !== 'published' && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                    {t(
                      'lyrraone.store.draftWarning',
                      'La formation doit être publiée (statut « Publié ») avant de configurer la vente.',
                    )}
                  </div>
                )}

                {lyrraOne && billingComplete === false && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                    {t(
                      'lyrraone.store.billingRequired',
                      'Renseignez vos informations de société avant de mettre en vente.',
                    )}{' '}
                    <Link
                      to={lyrraOneExtraSettingsTabPath('seller-billing')}
                      onClick={onClose}
                      className="font-semibold underline"
                    >
                      {t('lyrraone.store.openBillingSettings', 'Ouvrir Facturation & société')}
                    </Link>
                  </div>
                )}

                <div className="space-y-4">
                  <label className="block">
                    <span className={panelLabelClass}>{t('lyrraone.store.fieldTitle', 'Titre')}</span>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className={panelInputClass}
                      disabled={submitting}
                    />
                  </label>
                  <label className="block">
                    <span className={panelLabelClass}>{t('lyrraone.store.fieldDescription', 'Description')}</span>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className={cn(panelInputClass, 'resize-y')}
                      disabled={submitting}
                    />
                  </label>
                  <div className="block">
                    <span className={panelLabelClass}>{t('lyrraone.store.fieldCategory', 'Catégories')}</span>
                    <TagMultiInput
                      selected={tags}
                      suggestions={tagSuggestions}
                      onChange={setTags}
                      onCreateTag={(tag) => {
                        setTagSuggestions((prev) => (
                          prev.some((t) => t.toLowerCase() === tag.toLowerCase())
                            ? prev
                            : [...prev, tag].sort((a, b) => a.localeCompare(b, 'fr'))
                        ));
                      }}
                      disabled={submitting}
                      placeholder={t('lyrraone.store.categoryTagsPlaceholder', 'Ajouter une catégorie…')}
                      createLabel={(tag) => t('lyrraone.store.createCategoryTag', { tag, defaultValue: `Créer « ${tag} »` })}
                    />
                    <p className="mt-1.5 text-xs text-[color:var(--inst-player-text-muted,#6b7280)] dark:text-gray-400">
                      {t(
                        'lyrraone.store.categoryTagsHint',
                        'Sélectionnez une ou plusieurs catégories existantes ou créez-en de nouvelles.',
                      )}
                    </p>
                  </div>
                  <label className="block">
                    <span className={panelLabelClass}>{t('lyrraone.store.fieldPrice', 'Prix (€)')}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className={panelInputClass}
                      disabled={submitting}
                    />
                  </label>

                  {lyrraOne && (
                    <div className="rounded-xl border border-[color:var(--inst-player-border,#e5e7eb)] p-4 dark:border-gray-700">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Store className="h-4 w-4 shrink-0 text-[color:var(--inst-player-primary,#0C1C38)] dark:text-emerald-300" aria-hidden />
                            <p className="text-sm font-medium text-[color:var(--inst-player-text,#111827)] dark:text-gray-100">
                              {t('lyrraone.store.marketplaceToggle', 'Marketplace LyrraOne')}
                            </p>
                          </div>
                          <p className="mt-1 text-sm text-[color:var(--inst-player-text-muted,#6b7280)] dark:text-gray-400">
                            {t(
                              'lyrraone.store.marketplaceHint',
                              'Affichez votre formation dans le catalogue public LyrraOne, accessible à tous les apprenants.',
                            )}
                          </p>
                        </div>
                        <Switch
                          checked={listedOnMarketplace}
                          onChange={setListedOnMarketplace}
                          disabled={submitting || flowStatus !== 'published'}
                          size="md"
                          aria-label={t('lyrraone.store.marketplaceToggle', 'Marketplace LyrraOne')}
                        />
                      </div>
                      {listedOnMarketplace && listing?.id && (
                        <Link
                          to={`/store/${listing.id}`}
                          onClick={onClose}
                          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--inst-player-primary,#0C1C38)] hover:underline dark:text-emerald-300"
                        >
                          {t('lyrraone.store.viewOnMarketplace', 'Voir sur le marketplace')}
                        </Link>
                      )}
                    </div>
                  )}
                </div>

                {message && (
                  <div
                    className={cn(
                      'flex items-start gap-2 rounded-lg px-3 py-2 text-sm',
                      message.type === 'ok'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                        : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
                    )}
                  >
                    {message.type === 'ok' ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    ) : (
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    )}
                    {message.text}
                  </div>
                )}

                {(listing?.isPublished || lyrraOne) && flowStatus === 'published' && (
                  <div className="rounded-xl border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_22%,transparent)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,transparent)] px-3 py-3 text-sm dark:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,transparent)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,transparent)]">
                    <p className="font-medium text-[color:var(--inst-player-primary,#0C1C38)] dark:text-emerald-200">
                      {t(
                        'lyrraone.store.funnelHint',
                        'Créez un funnel de vente pour partager un lien direct à vos apprenants.',
                      )}
                    </p>
                    <Link
                      to="/funnels"
                      onClick={onClose}
                      className="mt-2 inline-flex items-center gap-1.5 font-semibold text-[color:var(--inst-player-primary,#0C1C38)] hover:underline dark:text-emerald-300"
                    >
                      <Megaphone className="h-4 w-4" aria-hidden />
                      {t('lyrraone.store.openFunnels', 'Gérer mes funnels')}
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-200 p-4 dark:border-gray-700">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="primary"
              className={btnPrimaryInst}
              onClick={() => void handlePublish()}
              loading={submitting}
              disabled={loading || (lyrraOne && billingComplete === false) || flowStatus !== 'published'}
            >
              {listing
                ? t('lyrraone.store.updateListing', 'Mettre à jour')
                : lyrraOne
                  ? t('lyrraone.store.saveOffer', 'Enregistrer')
                  : t('lyrraone.store.publish', 'Publier sur le Store')}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

/** @deprecated Utiliser PublishToStorePanel */
export const PublishToStoreModal = PublishToStorePanel;

export default PublishToStorePanel;
